import { computed, defineComponent, h } from 'vue'
import type { ComponentPublicInstance } from 'vue'

// Components
import { QIcon } from 'quasar'
import { BsClickable, BsTabStepper } from '../index'
import { emitModel } from '../emitModel'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { pushUrlHash } from '../../../composables/useUrlFilters'
import { BsTabDefinition } from '../types'
import { useTemplateRef } from '../useTemplateRef'

/**
 * The tab rail down the side of the wizards, wrapped around a BsTabStepper: the tabs navigate, the
 * stepper holds the panels and the forms in them.
 *
 * Navigating forward validates the step being left, which is the whole reason this owns the stepper ref —
 * `stepComplete` on a tab is computed once when the mode is set and goes stale, so a forward jump falls
 * back to asking the stepper to validate right now.
 */

/**
 * What this reaches for on the stepper it renders — Vue 3 types `$refs` entries loosely. Not optional: the
 * stepper is rendered unconditionally, so a click can only happen with the ref in place. The class
 * reached for it through `?.` under a `@ts-ignore`, which is the same thing without the types.
 */
type TabStepperRef = ComponentPublicInstance & { validateForm: () => Promise<boolean> }

export default defineComponent({
  name: 'BsTabbedLayout',

  // See BsInput: an undeclared event sharing a DOM event's name is bound as a native listener too, and
  // the caller's handler then runs a second time with a DOM event.
  emits: [ 'input', 'update:modelValue', 'done', 'return' ],

  props: {
    ...bsComponentProps,
    value: { type: Number, default: void 0 },
    tabs: {
      type: Array as unknown as () => BsTabDefinition[],
      default: void 0
    },
    title: { type: String, default: void 0 },
    nextStepNavigationOnly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    blockForwardNavigation: { type: Boolean, default: false },
    stepTitle: { type: String, default: void 0 }
  },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: bsTabStepper, set: setBsTabStepperRef } = useTemplateRef<any>()

    const vm = useVm()

    const bs = useBsComponent({ vm, props, emit, value: () => props.value })

    /**
     * The step this layout is on, under either name: `v-model="step"` compiles to `:modelValue` under
     * Vue 3, so the declared `value` prop is undefined for every caller that binds it that way and each
     * comparison below silently fell to `undefined`. `componentValue` resolves both (see
     * useBsComponent).
     */
    const currentStep = computed<number>(() => bs.componentValue.value as number)

    return {
      bsTabStepper,
      setBsTabStepperRef,
      currentStep,
      ...bs
    }
  },

  methods: {
    /** Renamed from `__renderHeader`: Vue does not proxy setup/method keys beginning with `_`. */
    renderHeader () {
      return h('h2', { class: 'bs-h4 bs-tabbed-layout__header' }, [this.title])
    },

    async onTabClick (option: BsTabDefinition) {
      if (this.disabled) return
      const currentOption = this.tabs.find(f => f.value === this.currentStep)

      // Look for the tab we're going to and if it's a previous one, skip validation.
      let navigationAllowed = !(this.blockForwardNavigation && option.value > this.currentStep) && (
        !this.nextStepNavigationOnly || // Continue if we don't care about limit navigation
        option.value <= this.currentStep + 1 || // OR we're going backwards (assume other steps already done are ok)
        currentOption?.stepComplete // Or the step we're on is marked as done already
      )

      // stepComplete can be stale (computed once in setMode). If it reports false but the user
      // is trying to jump forward, dynamically validate the current form as a fallback.
      if (
        !navigationAllowed &&
        this.nextStepNavigationOnly &&
        option.value > this.currentStep + 1 &&
        !(this.blockForwardNavigation && option.value > this.currentStep)
      ) {
        // Optional, as it was: the ref is gone once the stepper unmounts, and navigation is then
        // blocked rather than throwing.
        navigationAllowed = !!(await (this.bsTabStepper as TabStepperRef | undefined)?.validateForm())
      }

      if (navigationAllowed) {
        // The class guarded this with `option !== void 0`; every caller passes one, so only the
        // forward-navigation test survives.
        const requiresValidation = this.currentStep < option.value

        let outcome = true
        if (requiresValidation) {
          outcome = await (this.bsTabStepper as TabStepperRef | undefined)?.validateForm()
        }

        if (outcome || !requiresValidation) {
          // Update the url so when the page is refreshed it loads the same tab that the user was on —
          // through the router's history, or the author's next click puts it back (sc-28993).
          pushUrlHash(this.$router, option.value)
          emitModel(this, option.value)
        }
      }
    },

    renderTab (option: BsTabDefinition) {
      const
        isPastThisOption = option.value < this.currentStep,
        isActive = option.value === this.currentStep,
        isNextOption = option.value === this.currentStep + 1

      return option.hidden ? void 0 : h(BsClickable, {
        class: ['bs-tabbed-layout__tab', {
          'bs-tabbed-layout__tab--past': isPastThisOption,
          'bs-tabbed-layout__tab--active': isActive,
          'bs-tabbed-layout__tab--future': isPastThisOption === false && !isActive,
          'bs-tabbed-layout__tab--next-option': isNextOption,
          'bs-tabbed-layout__tab--is-edit': option.isEditMode,
          'cursor-pointer': option.stepComplete
        }],
        onClick: () => this.onTabClick(option)
      }, {
        // BsClickable is a component, so its children have to be a slot function rather than an array.
        default: () => [
          this.renderIcon(option, isPastThisOption, isActive),
          h('span', option.label)
        ]
      })
    },

    renderIcon (tab: BsTabDefinition, isPastThisOption: boolean, isActive: boolean) {
      if (tab.stepComplete !== false) {
        return h('div', { class: 'bs-tabbed-layout__tab-count' }, [
          h(QIcon, { name: 'app:check-filled', color: 'bs-g', size: '1.375rem' })
        ])
      }

      // NOTE, carried over: the class built a QIcon here and threw it away — an `h()` whose result is
      // never returned renders nothing. Left as it was; the block below is what actually renders.

      return h('div', { class: 'bs-tabbed-layout__tab-count' }, [
        h(QIcon, {
          name: isActive ? 'app:active' : isPastThisOption ? 'app:check-filled' : 'app:inactive',
          color: isActive || !isPastThisOption ? 'bs-grey-m' : 'bs-g',
          size: '1.375rem'
        })
      ])
    },

    renderTabs () {
      const node = []
      for (const option of this.tabs) {
        node.push(this.renderTab(option))
      }

      return h('div', { class: 'bs-tabbed-layout__tabs' }, [node])
    },

    renderContent () {
      return h('div', { class: 'bs-tabbed-layout__content' }, [
        this.renderTabs(),
        h('div', { class: 'bs-tabbed-layout__inner' }, [
          this.stepTitle && h('h1', { class: 'bs-h2' }, this.stepTitle),
          h(BsTabStepper, {
            ref: this.setBsTabStepperRef,
            value: this.currentStep,
            tabs: this.tabs,
            ...this.$attrs,
            onInput: (v: any) => emitModel(this, v),
            onDone: (v: any) => this.$emit('done', v),
            onReturn: () => this.$emit('return')
          }, {
            // The parent's slots, plus the default this component renders into the stepper. Vue 3
            // takes slots as the third argument and every entry must be a function.
            ...this.$slots,
            default: () => this.slot(this, 'default')
          })
        ])
      ])
    }
  },

  /**
   * Open on the step the url names — read from the address bar, not `$route.hash`. Every step change
   * writes the hash with `pushUrlHash` (`onTabClick` above, the stepper, the book and campaign forms),
   * which records the url in the router's history without navigating, so `$route.hash` stays whatever the
   * page was opened with.
   *
   * That only matters for a layout created mid-wizard, and under Vue 3 that happens. Vue 2 patched a
   * `v-if`/`v-else` pair of layouts in place; Vue 3 keys the branches apart, so the book wizard switching
   * between its create and import layouts builds a new one. Opened from the campaign wizard at `#1`,
   * "Import from Amazon" landed back on step 1, and so did a finished import (sc-28864).
   *
   * Nothing to read on the server: browsers never send the hash.
   */
  created () {
    if (process.env.SERVER) return

    const hash = window.location.hash
    if (hash !== '') {
      emitModel(this, parseInt(hash.replace('#', '')))
    }
  },

  render () {
    return h('div', {
      class: ['bs-tabbed-layout', {
        'bs-tabbed-layout--next-only': this.nextStepNavigationOnly,
        'bs-tabbed-layout--disabled': this.disabled
      }]
    }, [
      this.renderHeader(),
      this.renderContent()
    ])
  }
})
